import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { WorkItem } from './model';
import { formatTimestamp } from '../../utils/string-utils';

/**
 * Placeholder rendered whenever a field value is missing
 * (null, undefined, or empty string).
 */
const PLACEHOLDER = '—';

/**
 * Ordered list of the fields rendered by the Exception Details section.
 * Timestamps are flagged so they route through `formatTimestamp` before
 * `formatField`.
 *
 * `assignee`, `isShadowMode`, `process`, `updatedTime`,
 * `lastWorkItemStatusUpdatedTime`, `actionStatus`, and `description` are
 * intentionally excluded — those fields either duplicate data shown
 * elsewhere (e.g. `processPath` covers `process`, AGE on the list page is
 * derived from `lastWorkItemStatusUpdatedTime`) or default to placeholder
 * values often enough that surfacing them as their own row added more
 * noise than signal.
 */
type ExceptionField = {
    key: keyof WorkItem;
    label: string;
    isTimestamp?: boolean;
};

const EXCEPTION_FIELDS: ReadonlyArray<ExceptionField> = [
    { key: 'exceptionName', label: 'Exception Name' },
    { key: 'severity', label: 'Severity' },
    { key: 'status', label: 'Status' },
    { key: 'processPath', label: 'Process Path' },
    { key: 'operator', label: 'Operator' },
    { key: 'createdTime', label: 'Created Time', isTimestamp: true },
    { key: 'grainType', label: 'Grain Type' },
    { key: 'grainId', label: 'Grain Id' },
];

/**
 * Returns `"—"` for null, undefined, or empty-string values, else the
 * value coerced to a string. Trimming is intentionally NOT applied here
 * so a whitespace-only string still renders verbatim.
 */
const formatField = (value: unknown): string => {
    if (value === null || value === undefined || value === '') {
        return PLACEHOLDER;
    }
    return String(value);
};

interface ExceptionDetailsSectionProps {
    workItem: WorkItem;
}

const ExceptionDetailsSection: React.FC<ExceptionDetailsSectionProps> = ({ workItem }) => {
    return (
        <Column spacing="300">
            <Heading level={2}>Exception Details</Heading>
            {EXCEPTION_FIELDS.map(({ key, label, isTimestamp }) => {
                const raw = workItem[key];
                const displayValue = isTimestamp
                    ? formatField(formatTimestamp(raw as number | string | null | undefined))
                    : formatField(raw);

                return (
                    <Box key={String(key)} data-field={String(key)}>
                        <Row spacing="300" alignmentVertical="top" widths={['200px', 'fill']}>
                            <Text
                                type="b200"
                                color="secondary"
                                data-testid="exception-details-label"
                            >
                                {label}
                            </Text>
                            <Text
                                type="b200"
                                data-testid="exception-details-value"
                            >
                                {displayValue}
                            </Text>
                        </Row>
                    </Box>
                );
            })}
        </Column>
    );
};

export default ExceptionDetailsSection;
